import { useMemo } from 'react';
import { intl } from '../../../lib';
import type { LanguageProperty } from '../../generated/LanguageProperty';
import { useQuestionnaireStore } from '../../../lib/QuestionnaireStore';
import shallow from 'zustand/shallow';

type FormioI18nContextValue = {
  intlFmtTemplateStringWithOverride: (
    key: LanguageProperty,
    map: Record<string, string>,
    fallback?: string
  ) => string;
  intlWithOverride: (key: LanguageProperty, fallback?: string) => string;
  previewLocale?: string;
};

/**
 * Returns i18n helpers that check preview translations first.
 * still receive the correct preview language translations.
 */
export function useFormioI18n(): FormioI18nContextValue {
  const { previewTranslations, previewLocale } = useQuestionnaireStore(
    state => ({
      previewTranslations: state.previewTranslations,
      previewLocale: state.previewLocale
    }),
    shallow
  );

  return useMemo(
    () => ({
      intlWithOverride: (key, fallback) =>
        previewTranslations?.[key] ??
        intl(key, fallback ?? `${key}-NO_VALUE_AVAILABLE`),
      intlFmtTemplateStringWithOverride: (key, map, fallback) => {
        const template =
          previewTranslations?.[key] ??
          intl(key, fallback ?? `${key}-NO_VALUE_AVAILABLE`);
        return Object.keys(map).reduce(
          (result, token) => result.replace(`\${${token}}`, map[token]),
          template
        );
      },
      previewLocale
    }),
    [previewLocale, previewTranslations]
  );
}
